import type {
	HeaderData,
	HeaderMastheadData,
	NotificationsData,
	HeaderProfileData,
	HeaderStoreData,
} from '../../../../../components/blackrock/advisor-center/_types/header';
import type {
	Menu,
	MenuGroup,
	MenuItem,
} from '../../../../../components/blackrock/advisor-center/_types/navigation';

const getProfileData = (data: HeaderProfileData): HeaderProfileData => ({
	...data,
	// eslint-disable-next-line @typescript-eslint/ban-ts-comment
	// @ts-ignore
	editProfile: generateMenuItem(data?.editProfile),
	// eslint-disable-next-line @typescript-eslint/ban-ts-comment
	// @ts-ignore
	signIn: generateMenuItem(data?.signIn),
	// eslint-disable-next-line @typescript-eslint/ban-ts-comment
	// @ts-ignore
	signOut: generateMenuItem(data?.signOut),
});

const generateMenuItem = (data?: MenuItem): MenuItem | undefined => {
	return data?.displayValue && data?.href ? { ...data } : undefined;
};

const generateMenuItems = (data: MenuItem[]): MenuItem[] => {
	if (!data?.length) return [];
	const items = data.map(generateMenuItem);
	// eslint-disable-next-line @typescript-eslint/ban-ts-comment
	// @ts-ignore
	return items.filter((i) => i);
};

const generateMenuGroups = (data: MenuGroup[]): MenuGroup[] => {
	if (!data.length) return [];

	// eslint-disable-next-line @typescript-eslint/ban-ts-comment
	// @ts-ignore
	const groups: MenuGroup[] = data.map((group) => {
		// eslint-disable-next-line @typescript-eslint/ban-ts-comment
		// @ts-ignore
		if (!group.name || !group.menuItems.length) return undefined;

		return {
			name: group.name || '',
			// eslint-disable-next-line @typescript-eslint/ban-ts-comment
			// @ts-ignore
			menuItems: generateMenuItems(group.menuItems),
			cta: group.cta || null,
		};
	});

	return groups.filter((g) => g);
};

const getMyHubMenuData = (data: MenuItem): Menu[] => {
	if (!data?.displayValue) return [];

	return [
		{
			name: data.displayValue,
			menuGroups: [],
			active: data?.active || false,
			href: data?.href || '',
			additionalStyleClass: /*data?.additionalStyleClass ||*/ '',
			hideOnDesktop: false,
			icon: data?.icon ?? undefined,
			target: data?.target || '_self',
			ariaLabel: data?.ariaLabel || '',
			dataLinkEvent: data.dataLinkEvent || '',
		},
	];
};

const getMenuData = (data: Menu[]): Menu[] => {
	// eslint-disable-next-line @typescript-eslint/ban-ts-comment
	// @ts-ignore
	return (
		(data?.length &&
			data.map((menu) => ({
				name: menu?.name || '',
				// eslint-disable-next-line @typescript-eslint/ban-ts-comment
				// @ts-ignore
				menuGroups: generateMenuGroups(menu.menuGroups),
				active: menu?.active || false,
				href: menu?.href || '',
				additionalStyleClass: menu?.additionalStyleClass || '',
				hideOnDesktop: false,
				icon: {},
				target: '_self',
				ariaLabel: '',
				dataLinkEvent: '',
				spotlight: menu.spotlight || null,
			}))) ||
		[]
	);
};

const getNotificationsData = (
	notificationsData: NotificationsData | undefined,
	newCountLabel: string | undefined
): Menu[] => {
	if (!notificationsData) return [];

	const { menuItem } = notificationsData;

	return [
		{
			name: menuItem?.displayValue || 'Notifications',
			dataLinkEvent: menuItem?.dataLinkEvent || '',
			ariaLabel: menuItem?.ariaLabel || '',
			icon: {
				className: menuItem?.icon?.path ?? 'fa fa-bell',
				alt: menuItem?.icon?.alt ?? '',
				badge: newCountLabel,
			},
			// these are not relevant to notification menu
			menuGroups: [],
			active: false,
			href: '',
			additionalStyleClass: 'notification-link blk-ac-only-mobile',
			hideOnDesktop: true,
			target: '_self',
			isNotificationsMenu: true,
		},
	];
};

const getProfileMenuData = (
	data?: HeaderProfileData,
	hasMarginTop?: boolean
): Menu[] => {
	const userName = data?.userName;

	const editMenuItem = generateMenuItem(data?.editProfile);

	const signInMenuItem = generateMenuItem(data?.signIn);

	const signOutMenuItem = generateMenuItem(data?.signOut);

	const menuItems = [editMenuItem, signOutMenuItem].filter(
		(item) => !!item
	) as MenuItem[];

	return [
		{
			name: userName || signInMenuItem?.displayValue,
			menuGroups: userName
				? [
						{
							name: '',
							menuItems: menuItems,
						},
					]
				: [],
			href: userName ? '' : signInMenuItem?.href,
			icon: data?.icon,
			additionalStyleClass:
				'my-profile-link blk-ac-only-mobile' +
				(hasMarginTop ? ' with-margin-top' : ''),
			active: false,
			hideOnDesktop: true,
			target: '_self',
			ariaLabel: '',
			dataLinkEvent: '',
		},
	];
};

const getUserTypesMenuData = (data: HeaderMastheadData): Menu[] => {
	if (!data) return [];

	const currentUserTypeName = data?.userTypes?.find(({ active }) => active);
	const userTypeName = currentUserTypeName?.displayValue;
	const userTypeDataLink = currentUserTypeName?.dataLinkEvent;

	const userTypes = data?.userTypes
		?.map(generateMenuItem)
		// eslint-disable-next-line @typescript-eslint/ban-ts-comment
		// @ts-ignore
		?.filter(({ active }) => !active);

	return [
		{
			name: userTypeName,
			menuGroups: [
				{
					name: '',
					// eslint-disable-next-line @typescript-eslint/ban-ts-comment
					// @ts-ignore
					menuItems: userTypes,
				},
			],
			active: false,
			hideOnDesktop: true,
			href: '',
			additionalStyleClass: 'user-type-links blk-ac-only-mobile',
			target: '_self',
			ariaLabel: '',
			dataLinkEvent: userTypeDataLink || '',
		},
	];
};

const getSiteLinksMenuData = (data: HeaderMastheadData): Menu[] => {
	if (!data) return [];

	// eslint-disable-next-line @typescript-eslint/ban-ts-comment
	// @ts-ignore
	return data?.siteLinks.map((siteLink) => ({
		name: siteLink.displayValue || '',
		href: siteLink.href || '',
		menuGroups: [],
		additionalStyleClass: 'masthead-links blk-ac-only-mobile',
		active: false,
		hideOnDesktop: true,
		icon: {},
		target: '_self',
		ariaLabel: '',
		dataLinkEvent: siteLink.dataLinkEvent || '',
	}));
};

const getLocationsMenuData = (data: HeaderMastheadData): Menu[] => {
	if (!data || !data.countries) return [];

	const selectedCountry =
		data?.countries?.find(({ active }) => active) || data?.countries?.[0];

	const displayValue = selectedCountry?.displayValue || ' ';
	return [
		{
			name:
				displayValue +
				'<br/><span class="blk-ac-body-s-compact">' +
				data.countryMessage +
				'</span>',
			backLabel: displayValue,
			menuGroups: [
				{
					name: data?.countryMessage,
					menuItems: generateMenuItems(data?.countries),
				},
			],
			active: false,
			hideOnDesktop: true,
			href: '',
			additionalStyleClass: 'location-links blk-ac-only-mobile',
			target: '_self',
			ariaLabel: '',
			dataLinkEvent: selectedCountry?.dataLinkEvent || '',
		},
	];
};

const getAlertData = (data: MenuItem): MenuItem | undefined => {
	return generateMenuItem(data);
};

export const generateStoreData = (
	headerData?: HeaderData,
	unreadCount?: string
): HeaderStoreData => {
	const notificationData = getNotificationsData(
		headerData?.notificationsData,
		unreadCount
	);

	const hasProfileMenuMarginTop = notificationData?.length === 0;

	return {
		logo: headerData?.logo || {},
		userType: headerData?.ribbon || {},
		search: headerData?.searchBarData || {},
		// eslint-disable-next-line @typescript-eslint/ban-ts-comment
		// @ts-ignore
		profile: getProfileData(headerData?.profile),
		// eslint-disable-next-line @typescript-eslint/ban-ts-comment
		// @ts-ignore
		alert: getAlertData(headerData?.alert),

		menu: [
			// eslint-disable-next-line @typescript-eslint/ban-ts-comment
			// @ts-ignore
			...getMyHubMenuData(headerData?.myHub),
			// eslint-disable-next-line @typescript-eslint/ban-ts-comment
			// @ts-ignore
			...getMenuData(headerData?.menus),
			...getProfileMenuData(headerData?.profile, hasProfileMenuMarginTop),
			...notificationData,
			// eslint-disable-next-line @typescript-eslint/ban-ts-comment
			// @ts-ignore
			...getUserTypesMenuData(headerData?.mastheadData),
			// eslint-disable-next-line @typescript-eslint/ban-ts-comment
			// @ts-ignore
			...getLocationsMenuData(headerData?.mastheadData),
			// eslint-disable-next-line @typescript-eslint/ban-ts-comment
			// @ts-ignore
			...getSiteLinksMenuData(headerData?.mastheadData),
		],

		focusableElements: [],
	};
};
